import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { apiHelpers } from '@/lib/api';
import { 
  Clock, 
  User, 
  FileText, 
  Receipt, 
  Calendar,
  ClipboardCheck,
  Send,
  CheckCircle,
  DollarSign,
  Eye,
  Edit,
  Trash2,
  ArrowRight,
  RefreshCw
} from 'lucide-react';

// Map action types to icons and colors
const getActivityIcon = (action, objectType) => {
  const actionLower = action.toLowerCase();
  
  if (actionLower.includes('sent')) return { icon: Send, color: 'text-blue-500', bg: 'bg-blue-50' };
  if (actionLower.includes('approved') || actionLower.includes('payment')) return { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50' };
  if (actionLower.includes('created') || actionLower.includes('scheduled')) return { icon: FileText, color: 'text-purple-500', bg: 'bg-purple-50' };
  if (actionLower.includes('converted')) return { icon: ArrowRight, color: 'text-amber-500', bg: 'bg-amber-50' };
  if (actionLower.includes('updated')) return { icon: Edit, color: 'text-slate-500', bg: 'bg-slate-50' };
  if (actionLower.includes('deleted')) return { icon: Trash2, color: 'text-red-500', bg: 'bg-red-50' };
  if (actionLower.includes('viewed')) return { icon: Eye, color: 'text-slate-500', bg: 'bg-slate-50' };
  
  // Default by object type
  switch (objectType) {
    case 'site_visit': return { icon: ClipboardCheck, color: 'text-amber-500', bg: 'bg-amber-50' };
    case 'estimate': return { icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50' };
    case 'invoice': return { icon: Receipt, color: 'text-green-500', bg: 'bg-green-50' };
    case 'job': return { icon: Calendar, color: 'text-purple-500', bg: 'bg-purple-50' };
    default: return { icon: Clock, color: 'text-slate-500', bg: 'bg-slate-50' };
  }
};

const getObjectTypeLabel = (objectType) => {
  switch (objectType) {
    case 'site_visit': return 'Site Visit';
    case 'estimate': return 'Estimate';
    case 'invoice': return 'Invoice';
    case 'job': return 'Job';
    case 'client': return 'Customer';
    default: return objectType;
  }
};

const CustomerActivityHistory = ({ clientId }) => {
  const { organization } = useAuth();
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (organization?.id && clientId) {
      loadActivities();
    }
  }, [organization?.id, clientId]);

  const loadActivities = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiHelpers.getClientActivity(organization.id, clientId);
      setActivities(response.data.data || []);
    } catch (err) {
      console.error('Failed to load activity history:', err);
      setError('Failed to load activity history');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
        Loading activity history...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center text-red-500">
        <p>{error}</p>
        <button 
          onClick={loadActivities}
          className="mt-2 text-sm text-primary hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="p-6 text-center text-muted-foreground">
        <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>No activity recorded yet</p>
        <p className="text-sm">Actions like site visits, estimates, and invoices will appear here</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-border" data-testid="customer-activity-history">
      {/* Header */}
      <div className="px-4 py-3 bg-muted/30 flex items-center justify-between">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Clock className="w-4 h-4" />
          Activity History
        </h3>
        <span className="text-xs text-muted-foreground">{activities.length} events</span>
      </div>
      
      {/* Activity List */}
      <div className="max-h-[500px] overflow-y-auto">
        {activities.map((activity, index) => {
          const { icon: Icon, color, bg } = getActivityIcon(activity.action, activity.object_type);
          
          return (
            <div 
              key={activity.id || index}
              className="px-4 py-3 hover:bg-muted/20 transition-colors"
            >
              <div className="flex gap-3">
                {/* Icon */}
                <div className={`flex-shrink-0 w-8 h-8 rounded-full ${bg} flex items-center justify-center`}>
                  <Icon className={`w-4 h-4 ${color}`} />
                </div>
                
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">
                    {activity.action}
                  </p>
                  
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-muted-foreground">
                    {/* Object type badge */}
                    <span className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                      {getObjectTypeLabel(activity.object_type)}
                    </span>
                    
                    {/* User who performed action */}
                    {activity.user_name && (
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {activity.user_name}
                      </span>
                    )}
                    
                    {/* Timestamp */}
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {activity.timestamp_est || 'Unknown time'}
                    </span>
                  </div>
                  
                  {/* Details if any */}
                  {activity.details && (
                    <p className="mt-1 text-xs text-muted-foreground italic">
                      {activity.details}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CustomerActivityHistory;
